// ===========================================
// Reports React Query Hooks
// ===========================================
// Custom hooks for attendance, revenue, student progress, and teacher performance reports
// Phase 2 Priority 12: Reporting & Analytics

import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { familyPaymentKeys } from './useFamilyPayments';
import {
  registrationFeesNotAppliedApi,
  type RegistrationFeesNotAppliedReport,
} from '../api/reports.api';
import { emailLogReportApi, type EmailLogReportFilters, type EmailLogReportData } from '../api/reports.api';
import { retroEditsReportApi, type RetroEditsReportFilters, type RetroEditsReportData } from '../api/reports.api';
import {
  reportsApi,
  AttendanceReportData,
  AttendanceReportFilters,
  AttendanceTrendData,
  RevenueReportData,
  RevenueReportFilters,
  StudentProgressReportData,
  StudentProgressFilters,
  TeacherPerformanceReportData,
  TeacherPerformanceFilters,
  ReportExportFormat,
  HybridBookingReportFilters,
  EnrollmentReportFilters,
  OperationsFilters,
} from '../api/reports.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const reportsKeys = {
  all: ['reports'] as const,
  attendance: {
    all: ['reports', 'attendance'] as const,
    report: (filters?: AttendanceReportFilters) =>
      [...reportsKeys.attendance.all, 'report', filters] as const,
    trends: (filters?: AttendanceReportFilters) =>
      [...reportsKeys.attendance.all, 'trends', filters] as const,
  },
  revenue: {
    all: ['reports', 'revenue'] as const,
    report: (filters?: RevenueReportFilters) =>
      [...reportsKeys.revenue.all, 'report', filters] as const,
  },
  studentProgress: {
    all: ['reports', 'student-progress'] as const,
    report: (filters?: StudentProgressFilters) =>
      [...reportsKeys.studentProgress.all, 'report', filters] as const,
  },
  teacherPerformance: {
    all: ['reports', 'teacher-performance'] as const,
    report: (filters?: TeacherPerformanceFilters) =>
      [...reportsKeys.teacherPerformance.all, 'report', filters] as const,
  },
  hybridBooking: {
    all: ['reports', 'hybrid-booking'] as const,
    metrics: (filters?: HybridBookingReportFilters) =>
      [...reportsKeys.hybridBooking.all, 'metrics', filters] as const,
    completion: (filters?: HybridBookingReportFilters) =>
      [...reportsKeys.hybridBooking.all, 'completion', filters] as const,
    timeSlots: (filters?: HybridBookingReportFilters) =>
      [...reportsKeys.hybridBooking.all, 'time-slots', filters] as const,
    trends: (filters?: HybridBookingReportFilters) =>
      [...reportsKeys.hybridBooking.all, 'trends', filters] as const,
    cancellations: (filters?: HybridBookingReportFilters) =>
      [...reportsKeys.hybridBooking.all, 'cancellations', filters] as const,
  },
  enrollment: {
    all: ['reports', 'enrollment'] as const,
    trends: (months?: number) =>
      [...reportsKeys.enrollment.all, 'trends', months] as const,
    retention: (filters?: EnrollmentReportFilters) =>
      [...reportsKeys.enrollment.all, 'retention', filters] as const,
    programLevels: () =>
      [...reportsKeys.enrollment.all, 'program-levels'] as const,
    byType: (filters?: EnrollmentReportFilters) =>
      [...reportsKeys.enrollment.all, 'by-type', filters] as const,
  },
  operations: {
    all: ['reports', 'operations'] as const,
    roomUtilization: (filters?: OperationsFilters) =>
      [...reportsKeys.operations.all, 'room-utilization', filters] as const,
    peakHours: (filters?: OperationsFilters) =>
      [...reportsKeys.operations.all, 'peak-hours', filters] as const,
    teacherWorkload: (filters?: OperationsFilters) =>
      [...reportsKeys.operations.all, 'teacher-workload', filters] as const,
    extraLessonMetrics: (filters?: OperationsFilters) =>
      [...reportsKeys.operations.all, 'extra-lesson-metrics', filters] as const,
    meetGreetConversion: (months?: number) =>
      [...reportsKeys.operations.all, 'meet-greet-conversion', months] as const,
    enquiryFunnel: (months?: number) =>
      [...reportsKeys.operations.all, 'enquiry-funnel', months] as const,
  },
};

// ===========================================
// ATTENDANCE REPORT HOOKS
// ===========================================

/**
 * Get attendance report with summary, breakdown, and trends
 * Caches for 60 seconds (reports are less frequently updated than dashboards)
 */
export function useAttendanceReport(filters?: AttendanceReportFilters) {
  return useQuery<AttendanceReportData>({
    queryKey: reportsKeys.attendance.report(filters),
    queryFn: () => reportsApi.getAttendanceReport(filters),
    staleTime: 60000, // 1 minute
    enabled: filters !== undefined, // Only fetch when filters are set
  });
}

/**
 * Get attendance trends (weekly aggregation)
 * Used for chart data visualization
 */
export function useAttendanceTrends(filters?: AttendanceReportFilters) {
  return useQuery<AttendanceTrendData[]>({
    queryKey: reportsKeys.attendance.trends(filters),
    queryFn: () => reportsApi.getAttendanceTrends(filters),
    staleTime: 60000,
    enabled: filters !== undefined,
  });
}

/**
 * Export attendance report as CSV or PDF
 * Triggers browser download on success
 */
export function useExportAttendanceReport() {
  return useMutation({
    mutationFn: ({
      format,
      filters,
    }: {
      format: ReportExportFormat;
      filters?: AttendanceReportFilters;
    }) => reportsApi.exportAttendanceReport(format, filters),
    onSuccess: (blob, { format }) => {
      // Trigger file download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Attendance report export failed:', error);
      // Toast notification would go here
    },
  });
}

// ===========================================
// REVENUE REPORT HOOKS (Future Implementation)
// ===========================================

/**
 * Get revenue report with summary, outstanding invoices, and trends
 */
export function useRevenueReport(filters?: RevenueReportFilters) {
  return useQuery<RevenueReportData>({
    queryKey: reportsKeys.revenue.report(filters),
    queryFn: () => reportsApi.getRevenueReport(filters),
    staleTime: 60000,
    enabled: filters !== undefined,
  });
}

/**
 * Get outstanding invoices with aging buckets
 */
export function useOutstandingInvoices() {
  return useQuery({
    queryKey: ['reports', 'revenue', 'outstanding'],
    queryFn: () => reportsApi.getOutstandingInvoices(),
    staleTime: 60000,
  });
}

/**
 * Get Monthly Recurring Revenue trends
 */
export function useMRRTrends(months: number = 12) {
  return useQuery({
    queryKey: ['reports', 'revenue', 'mrr', months],
    queryFn: () => reportsApi.getMRRTrends(months),
    staleTime: 60000,
  });
}

/**
 * Get Average Revenue Per Student
 */
export function useARPS(termId?: string) {
  return useQuery({
    queryKey: ['reports', 'revenue', 'arps', termId],
    // The backend requires a termId (400 without one); `enabled` below
    // guarantees this only runs once we have one.
    queryFn: () => reportsApi.getARPS(termId!),
    staleTime: 60000,
    enabled: termId !== undefined,
  });
}

/**
 * Get payment method breakdown
 */
export function usePaymentMethodBreakdown(termId?: string) {
  return useQuery({
    queryKey: ['reports', 'revenue', 'payment-methods', termId],
    queryFn: () => reportsApi.getPaymentMethodBreakdown(termId),
    staleTime: 60000,
  });
}

/**
 * Export revenue report as CSV or PDF
 * Triggers browser download on success
 */
export function useExportRevenueReport() {
  return useMutation({
    mutationFn: ({
      format,
      filters,
    }: {
      format: ReportExportFormat;
      filters?: RevenueReportFilters;
    }) => reportsApi.exportRevenueReport(format, filters),
    onSuccess: (blob, { format }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `revenue-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Revenue report export failed:', error);
    },
  });
}

/**
 * Export outstanding invoices as CSV or PDF
 * Triggers browser download on success
 */
export function useExportOutstandingInvoices() {
  return useMutation({
    mutationFn: (format: ReportExportFormat) => reportsApi.exportOutstandingInvoices(format),
    onSuccess: (blob, format) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `outstanding-invoices-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Outstanding invoices export failed:', error);
    },
  });
}

// ===========================================
// STUDENT PROGRESS REPORT HOOKS (Future Implementation)
// ===========================================

/**
 * Get student progress report with summary, individual student details, and breakdowns
 */
export function useStudentProgressReport(filters?: StudentProgressFilters) {
  return useQuery<StudentProgressReportData>({
    queryKey: reportsKeys.studentProgress.report(filters),
    queryFn: () => reportsApi.getStudentProgressReport(filters),
    staleTime: 60000,
    enabled: filters !== undefined,
  });
}

/**
 * Get student progress grouped by program level
 */
export function useStudentProgressByLevel(termId?: string) {
  return useQuery({
    queryKey: ['reports', 'student-progress', 'by-level', termId],
    queryFn: () => reportsApi.getStudentProgressByLevel(termId),
    staleTime: 60000,
  });
}

/**
 * Get piece completion rates
 */
export function useStudentPieceCompletionRates(filters?: { termId?: string; instrumentId?: string }) {
  return useQuery({
    queryKey: ['reports', 'student-progress', 'piece-completion', filters],
    queryFn: () => reportsApi.getStudentPieceCompletionRates(filters),
    staleTime: 60000,
  });
}

/**
 * Get student streak analysis
 */
export function useStudentStreakAnalysis(termId?: string) {
  return useQuery({
    queryKey: ['reports', 'student-progress', 'streaks', termId],
    queryFn: () => reportsApi.getStudentStreakAnalysis(termId),
    staleTime: 60000,
  });
}

/**
 * Export student progress report as CSV or PDF
 * Triggers browser download on success
 */
export function useExportStudentProgressReport() {
  return useMutation({
    mutationFn: ({
      format,
      filters,
    }: {
      format: ReportExportFormat;
      filters?: StudentProgressFilters;
    }) => reportsApi.exportStudentProgressReport(format, filters),
    onSuccess: (blob, { format }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `student-progress-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Student progress report export failed:', error);
    },
  });
}

// ===========================================
// TEACHER PERFORMANCE REPORT HOOKS (Future Implementation)
// ===========================================

/**
 * Get teacher performance report with summary and individual teacher metrics
 */
export function useTeacherPerformanceReport(filters?: TeacherPerformanceFilters) {
  return useQuery<TeacherPerformanceReportData>({
    queryKey: reportsKeys.teacherPerformance.report(filters),
    queryFn: () => reportsApi.getTeacherPerformanceReport(filters),
    staleTime: 60000,
    enabled: filters !== undefined,
  });
}

/**
 * Get teacher attendance metrics
 */
export function useTeacherAttendanceMetrics(filters?: { teacherId?: string; termId?: string }) {
  return useQuery({
    queryKey: ['reports', 'teacher-performance', 'attendance', filters],
    queryFn: () => reportsApi.getTeacherAttendanceMetrics(filters),
    staleTime: 60000,
  });
}

/**
 * Get teacher student retention rates
 */
export function useTeacherStudentRetention(filters?: { teacherId?: string; termId?: string }) {
  return useQuery({
    queryKey: ['reports', 'teacher-performance', 'retention', filters],
    queryFn: () => reportsApi.getTeacherStudentRetention(filters),
    staleTime: 60000,
  });
}

/**
 * Get teacher revenue contribution
 */
export function useTeacherRevenueContribution(filters?: { teacherId?: string; termId?: string }) {
  return useQuery({
    queryKey: ['reports', 'teacher-performance', 'revenue', filters],
    queryFn: () => reportsApi.getTeacherRevenueContribution(filters),
    staleTime: 60000,
  });
}

/**
 * Export teacher performance report as CSV or PDF
 * Triggers browser download on success
 */
export function useExportTeacherPerformanceReport() {
  return useMutation({
    mutationFn: ({
      format,
      filters,
    }: {
      format: ReportExportFormat;
      filters?: TeacherPerformanceFilters;
    }) => reportsApi.exportTeacherPerformanceReport(format, filters),
    onSuccess: (blob, { format }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `teacher-performance-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Teacher performance report export failed:', error);
    },
  });
}

// ===========================================
// UTILITY FUNCTIONS
// ===========================================

/**
 * Format currency from CENTS to display string.
 * Re-exported from the shared currency util under this hook's historical name so
 * report callers keep working; new code should import `formatCurrencyFromCents`
 * (or `formatCurrencyFromDollars`) directly to make the unit explicit.
 */
export { formatCurrencyFromCents as formatCurrency } from '../utils/currency';

/**
 * Format percentage for display
 */
export function formatPercentage(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Parse ISO date string to YYYY-MM-DD format for date inputs
 */
export function toDateInputValue(dateString?: string): string {
  if (!dateString) return '';
  return dateString.split('T')[0];
}

/**
 * Convert Date object to ISO string for API
 */
export function toISODateString(date?: Date): string | undefined {
  if (!date) return undefined;
  return date.toISOString();
}

// ===========================================
// HYBRID BOOKING REPORT HOOKS
// ===========================================

/**
 * Get hybrid booking metrics summary
 * Returns overall statistics for hybrid booking system
 */
export function useHybridBookingMetrics(filters?: HybridBookingReportFilters) {
  return useQuery({
    queryKey: reportsKeys.hybridBooking.metrics(filters),
    queryFn: () => reportsApi.getHybridBookingMetrics(filters),
    staleTime: 60000,
    // The API answers 400 without a term; do not ask until there is one.
    enabled: !!filters?.termId,
  });
}

/**
 * Get booking completion rates by week
 */
export function useBookingCompletionRates(filters?: HybridBookingReportFilters) {
  return useQuery({
    queryKey: reportsKeys.hybridBooking.completion(filters),
    queryFn: () => reportsApi.getBookingCompletionRates(filters),
    staleTime: 60000,
    // The API answers 400 without a term; do not ask until there is one.
    enabled: !!filters?.termId,
  });
}

/**
 * Get time slot popularity heatmap data
 */
export function useTimeSlotPopularity(filters?: HybridBookingReportFilters) {
  return useQuery({
    queryKey: reportsKeys.hybridBooking.timeSlots(filters),
    queryFn: () => reportsApi.getTimeSlotPopularity(filters),
    staleTime: 60000,
    // The API answers 400 without a term; do not ask until there is one.
    enabled: !!filters?.termId,
  });
}

/**
 * Get booking trends over time
 */
export function useBookingTrends(filters?: HybridBookingReportFilters) {
  return useQuery({
    queryKey: reportsKeys.hybridBooking.trends(filters),
    queryFn: () => reportsApi.getBookingTrends(filters),
    staleTime: 60000,
    // The API answers 400 without a term; do not ask until there is one.
    enabled: !!filters?.termId,
  });
}

/**
 * Get cancellation patterns analysis
 */
export function useCancellationPatterns(filters?: HybridBookingReportFilters) {
  return useQuery({
    queryKey: reportsKeys.hybridBooking.cancellations(filters),
    queryFn: () => reportsApi.getCancellationPatterns(filters),
    staleTime: 60000,
    // The API answers 400 without a term; do not ask until there is one.
    enabled: !!filters?.termId,
  });
}

// ===========================================
// ENROLLMENT REPORT HOOKS
// ===========================================

/**
 * Get enrollment trends over time
 */
export function useEnrollmentTrends(months: number = 12) {
  return useQuery({
    queryKey: reportsKeys.enrollment.trends(months),
    queryFn: () => reportsApi.getEnrollmentTrends(months),
    staleTime: 60000,
  });
}

/**
 * Get student retention rates
 */
export function useRetentionRates(filters?: EnrollmentReportFilters) {
  return useQuery({
    queryKey: reportsKeys.enrollment.retention(filters),
    queryFn: () => reportsApi.getRetentionRates(filters),
    staleTime: 60000,
  });
}

/**
 * Get program level distribution
 */
export function useProgramLevelDistribution() {
  return useQuery({
    queryKey: reportsKeys.enrollment.programLevels(),
    queryFn: () => reportsApi.getProgramLevelDistribution(),
    staleTime: 60000,
  });
}

/**
 * Get enrollment distribution by lesson type
 */
export function useEnrollmentByLessonType(filters?: EnrollmentReportFilters) {
  return useQuery({
    queryKey: reportsKeys.enrollment.byType(filters),
    queryFn: () => reportsApi.getEnrollmentByLessonType(filters),
    staleTime: 60000,
  });
}

// ===========================================
// OPERATIONS REPORT HOOKS
// ===========================================

/**
 * Get room utilization metrics
 */
export function useRoomUtilization(filters?: OperationsFilters) {
  return useQuery({
    queryKey: reportsKeys.operations.roomUtilization(filters),
    queryFn: () => reportsApi.getRoomUtilization(filters),
    staleTime: 60000,
  });
}

/**
 * Get peak hour booking patterns
 */
export function usePeakHours(filters?: OperationsFilters) {
  return useQuery({
    queryKey: reportsKeys.operations.peakHours(filters),
    queryFn: () => reportsApi.getPeakHours(filters),
    staleTime: 60000,
  });
}

/**
 * Get teacher workload distribution
 */
export function useTeacherWorkload(filters?: OperationsFilters) {
  return useQuery({
    queryKey: reportsKeys.operations.teacherWorkload(filters),
    queryFn: () => reportsApi.getTeacherWorkload(filters),
    staleTime: 60000,
  });
}

/**
 * Get extra lesson request metrics
 */
export function useExtraLessonMetrics(filters?: OperationsFilters) {
  return useQuery({
    queryKey: reportsKeys.operations.extraLessonMetrics(filters),
    queryFn: () => reportsApi.getExtraLessonMetrics(filters),
    staleTime: 60000,
  });
}

/**
 * Get meet & greet conversion funnel
 */
export function useMeetGreetConversion(months: number = 12) {
  return useQuery({
    queryKey: reportsKeys.operations.meetGreetConversion(months),
    queryFn: () => reportsApi.getMeetGreetConversion(months),
    staleTime: 60000,
  });
}

export function useEnquiryFunnel(months: number = 12) {
  return useQuery({
    queryKey: reportsKeys.operations.enquiryFunnel(months),
    queryFn: () => reportsApi.getEnquiryFunnel(months),
    staleTime: 60000,
  });
}

// ===========================================
// OVERVIEW HOOKS - For Reports Dashboard
// ===========================================

/**
 * Get attendance overview summary
 * Returns key metrics for the overview dashboard
 */
export function useAttendanceOverview(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'overview', 'attendance'],
    queryFn: async () => {
      const data = await reportsApi.getAttendanceReport();
      return {
        overallAttendanceRate: data.summary.attendanceRate,
        totalLessons: data.summary.totalLessons,
        totalAbsences: data.summary.absentCount,
      };
    },
    staleTime: 60000,
  });
}

/**
 * Get revenue overview summary
 */
export function useRevenueOverview(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'overview', 'revenue'],
    queryFn: async () => {
      const report = await reportsApi.getRevenueReport();
      const mrr = await reportsApi.getMRRTrends(1);
      // Field names must match what the backend actually emits (see
      // services/reports/revenue.report.ts): the summary carries
      // `totalRevenue`/`averageInvoiceValue`, and an MRR row carries `revenue`.
      // Reading the wrong names silently yielded `undefined` on every tile.
      return {
        totalRevenue: report.summary.totalRevenue,
        mrr: mrr[0]?.revenue ?? 0,
        averageInvoiceValue: report.summary.averageInvoiceValue,
      };
    },
    staleTime: 60000,
  });
}

/**
 * Get outstanding invoices overview
 */
export function useOutstandingInvoicesOverview(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'overview', 'outstanding-invoices'],
    queryFn: async () => {
      const invoices = await reportsApi.getOutstandingInvoices();
      const over90 = invoices
        .filter(inv => inv.agingBucket === '90+')
        .reduce((sum, inv) => sum + inv.amountDue, 0);
      const totalOutstanding = invoices.reduce((sum, inv) => sum + inv.amountDue, 0);
      return {
        totalOutstanding,
        invoiceCount: invoices.length,
        over90Days: over90,
      };
    },
    staleTime: 60000,
  });
}

/**
 * Get student progress overview
 */
export function useStudentProgressOverview(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'overview', 'student-progress'],
    queryFn: async () => {
      const report = await reportsApi.getStudentProgressReport();
      return {
        averageProgress: report.summary.averageProgressRate,
        piecesCompleted: report.summary.totalPiecesCompleted,
        activeStudents: report.summary.totalStudents,
      };
    },
    staleTime: 60000,
  });
}

/**
 * Get teacher performance overview
 */
export function useTeacherPerformanceOverview(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'overview', 'teacher-performance'],
    queryFn: async () => {
      const report = await reportsApi.getTeacherPerformanceReport();
      return {
        outstandingTasks: report.summary.outstandingTasks?.total ?? 0,
        avgAttendanceRate: report.summary.averageAttendanceRate,
        totalTeachers: report.summary.totalTeachers,
        totalClasses: report.summary.totalLessonsDelivered,
      };
    },
    staleTime: 60000,
  });
}

/**
 * Get hybrid booking overview
 */
export function useHybridBookingOverview(termId?: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['reports', 'overview', 'hybrid-booking', termId],
    // ⚠️ BOTH conditions. The termId gate is pre-existing (the metrics endpoint
    // 400s without one); `enabled` is the card-visibility gate. Dropping either
    // reintroduces a bug: without termId a doomed request fires, and without
    // `enabled` a hidden card still fetches.
    // Term-shaped: the metrics endpoint requires one (400 without). The overview
    // passes the school's CURRENT term — before 2026-09-10 it passed nothing and
    // the card sat empty forever.
    enabled: enabled && !!termId,
    queryFn: async () => {
      const metrics = await reportsApi.getHybridBookingMetrics({ termId });
      return {
        completionRate: metrics.summary.overallCompletionRate,
        totalBookings: metrics.summary.totalBookingsMade,
        avgBookingTime: metrics.summary.averageBookingTime,
      };
    },
    staleTime: 60000,
  });
}

/**
 * Get operations overview
 */
export function useOperationsOverview(enabled: boolean = true) {
  return useQuery({
    enabled,
    queryKey: ['reports', 'overview', 'operations'],
    queryFn: async () => {
      const roomUtil = await reportsApi.getRoomUtilization();
      const meetGreet = await reportsApi.getMeetGreetConversion(1);
      const extraLessons = await reportsApi.getExtraLessonMetrics();

      const avgUtilization = roomUtil.reduce((sum, r) => sum + r.utilizationRate, 0) / (roomUtil.length || 1);

      return {
        avgRoomUtilization: avgUtilization,
        meetGreetConversion: meetGreet[0]?.conversionRate || 0,
        extraLessonRequests: extraLessons.totalRequests,
      };
    },
    staleTime: 60000,
  });
}

// ===========================================
// EMAIL LOG REPORT HOOKS (2026-09-09)
// ===========================================

export const emailLogKeys = {
  all: ['reports', 'email-log'] as const,
  report: (filters?: EmailLogReportFilters) => [...emailLogKeys.all, 'report', filters] as const,
};

/**
 * The email log for a period. `keepPreviousData` so paging does not blank the
 * table between pages; `enabled` only once filters exist.
 */
export function useEmailLogReport(filters?: EmailLogReportFilters, enabled: boolean = true) {
  return useQuery<EmailLogReportData>({
    queryKey: emailLogKeys.report(filters),
    queryFn: () => emailLogReportApi.get(filters),
    staleTime: 60000,
    enabled: enabled && filters !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** Export the email log as CSV (every row) or PDF (summary). Triggers a download. */
export function useExportEmailLogReport() {
  return useMutation({
    mutationFn: ({ format, filters }: { format: ReportExportFormat; filters?: EmailLogReportFilters }) =>
      emailLogReportApi.export(format, filters),
    onSuccess: (blob, { format }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `email-log-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Email log export failed:', error);
    },
  });
}

// ===========================================
// RETRO-EDIT REPORT HOOKS (2026-09-18)
// ===========================================

export const retroEditsKeys = {
  all: ['reports', 'retro-edits'] as const,
  report: (filters?: RetroEditsReportFilters) => [...retroEditsKeys.all, 'report', filters] as const,
};

/**
 * The retro-edit audit for a period. `keepPreviousData` so paging does not
 * blank the table between pages; `enabled` only once filters exist.
 */
export function useRetroEditsReport(filters?: RetroEditsReportFilters, enabled: boolean = true) {
  return useQuery<RetroEditsReportData>({
    queryKey: retroEditsKeys.report(filters),
    queryFn: () => retroEditsReportApi.get(filters),
    staleTime: 60000,
    enabled: enabled && filters !== undefined,
    placeholderData: keepPreviousData,
  });
}

/** Export the retro-edit audit as CSV (every row) or PDF (summary + people). Triggers a download. */
export function useExportRetroEditsReport() {
  return useMutation({
    mutationFn: ({ format, filters }: { format: ReportExportFormat; filters?: RetroEditsReportFilters }) =>
      retroEditsReportApi.export(format, filters),
    onSuccess: (blob, { format }) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `retro-edits-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Retro-edit export failed:', error);
    },
  });
}

// ===========================================
// #374 Phase 2: registration fees not yet applied
// ===========================================

// Nested UNDER familyPaymentKeys.all (itself under invoiceKeys.all): Assign to child, recording or
// deleting a fee and every invoice change already invalidate that prefix, so this report refreshes
// with no second wire-up.
export const registrationFeesNotAppliedKeys = {
  report: () => [...familyPaymentKeys.all, 'registration-fees-not-applied'] as const,
};

/** Pass `enabled: can('registration_fees', 'read')` (#272). */
export function useRegistrationFeesNotApplied(enabled: boolean) {
  return useQuery<RegistrationFeesNotAppliedReport>({
    queryKey: registrationFeesNotAppliedKeys.report(),
    queryFn: () => registrationFeesNotAppliedApi.get(),
    enabled,
  });
}

/** The CSV download (same gate). */
export function useExportRegistrationFeesNotApplied() {
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: () => registrationFeesNotAppliedApi.exportCsv(),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `registration-fees-not-applied-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () =>
      enqueueSnackbar('Could not download the registration fees report. Please try again.', { variant: 'error' }),
  });
}
